import React from 'react';

import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Form, Section, ToggleContainer, Chip } from '@weezevent/nacre';
import Styles from '../../styles/eventCreation/eventCreation.module.css';
import CountrySelector from '../../nacre/components/countrySelector';
import Phone from '../../components/phone/phone';

function ContactIcons({ visible = [] }) {
    const names = ['desktop', 'envelope', 'tickets'];
    return (
        <div className={Styles['contact-field-icons']}>
            {names.map(name => (
                //we put 'text={" "}' in order to have the chip load with its full size
                <Chip key={name} icon={name} text={' '} primary className={`${!visible.includes(name) ? Styles['contact-field-icons__hidden'] : ''}`} />
            ))}
        </div>
    );
}

export default function EventCreationContact({ formContact, defaultFormContact, onChange, eventErrors }) {
    const { t } = useTranslation();

    const onChangeUseContact = React.useCallback(
        value => {
            if (!value) {
                //if use_contact = false, reload defaultFormContact
                return onChange('contact_data', { ...formContact, ...defaultFormContact }, true);
            }
            onChange('contact_data', { ...formContact, use_contact: value });
        },
        [formContact, onChange, defaultFormContact]
    );

    const onChangeContact = React.useCallback(
        (name, value) => {
            onChange('contact_data', { ...formContact, [name]: value });
        },
        [formContact, onChange]
    );

    const onChangeAddress = React.useCallback(
        (name, value) => {
            onChange('contact_data', { ...formContact, address: { ...formContact.address, [name]: value } });
        },
        [formContact, onChange]
    );

    return (
        <>
            <Section className={Styles['contact-form-container-header']}>
                <div className={Styles['contact-form-title-header']}>{t('ticket.common.legend')}</div>
                <div className={Styles['contact-form-icons-header']}>
                    <Chip icon="desktop" primary text={t('ticket.creationEvent.contact.icons.mini-site')} />
                    <Chip icon="envelope" primary text={t('ticket.creationEvent.contact.icons.email')} />
                    <Chip icon="tickets" primary text={t('ticket.creationEvent.contact.icons.ticket')} />
                </div>
            </Section>
            <ToggleContainer
                title={t('ticket.creationEvent.contact.use_contact')}
                className={Styles['contact-toggle']}
                toggle={formContact.use_contact}
                onToggle={value => onChangeUseContact(value)}
            />
            <Section className={Styles['contact-creating-container']}>
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.entity')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Form.Input
                            name="entity"
                            value={formContact.entity || ''}
                            onChange={({ target: { name, value } }) => {
                                onChangeContact(name, value);
                            }}
                            disabled={!formContact.use_contact}
                        />
                        <ContactIcons visible={['desktop', 'envelope', 'tickets']} />
                    </div>
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.licence')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Form.Input
                            name="licence"
                            value={formContact.licence || ''}
                            onChange={({ target: { name, value } }) => {
                                onChangeContact(name, value);
                            }}
                            disabled={!formContact.use_contact}
                        />
                        <ContactIcons visible={['desktop', 'tickets']} />
                    </div>
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.email')} />
                    <div className={Styles['contact-field-container']}>
                        <Form.Input
                            name="email"
                            value={formContact.email || ''}
                            onChange={({ target: { name, value } }) => {
                                onChangeContact(name, value);
                            }}
                            disabled={!formContact.use_contact}
                        />
                        <ContactIcons visible={['desktop', 'envelope', 'tickets']} />
                    </div>
                </Form.Field>
                <Form.FieldErrors errors={[t(eventErrors['contact_data']?.email)]} />
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.last_name')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Form.Input
                            name="last_name"
                            value={formContact.last_name || ''}
                            onChange={({ target: { name, value } }) => {
                                onChangeContact(name, value);
                            }}
                            disabled={!formContact.use_contact}
                        />
                        <ContactIcons visible={['desktop']} />
                    </div>
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.first_name')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Form.Input
                            name="first_name"
                            value={formContact.first_name || ''}
                            onChange={({ target: { name, value } }) => {
                                onChangeContact(name, value);
                            }}
                            disabled={!formContact.use_contact}
                        />
                        <ContactIcons visible={['desktop']} />
                    </div>
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.function')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Form.Input
                            name="function"
                            value={formContact && formContact['function'] ? formContact['function'] : ''} //ternary for resolve error linter
                            onChange={({ target: { name, value } }) => {
                                onChangeContact(name, value);
                            }}
                            disabled={!formContact.use_contact}
                        />
                        <ContactIcons visible={['desktop']} />
                    </div>
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.address')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Form.Input
                            name="address1"
                            value={formContact.address?.address1 || ''}
                            onChange={({ target: { name, value } }) => {
                                onChangeAddress(name, value);
                            }}
                            disabled={!formContact.use_contact}
                        />
                        <ContactIcons visible={['desktop', 'envelope']} />
                    </div>
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.zip_code')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Form.Input
                            name="zip_code"
                            value={formContact.address?.zip_code || ''}
                            onChange={({ target: { name, value } }) => {
                                onChangeAddress(name, value);
                            }}
                            disabled={!formContact.use_contact}
                        />
                        <ContactIcons visible={['desktop', 'envelope']} />
                    </div>
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.city')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Form.Input
                            name="city"
                            value={formContact.address?.city || ''}
                            onChange={({ target: { name, value } }) => {
                                onChangeAddress(name, value);
                            }}
                            disabled={!formContact.use_contact}
                        />
                        <ContactIcons visible={['desktop', 'envelope']} />
                    </div>
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.event.country')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-country']}>
                        <CountrySelector
                            placeholder={t('ticket.event.country_placeholder')}
                            value={formContact.address?.country || ''}
                            onChange={value => {
                                onChangeAddress('country', value);
                            }}
                            disabled={!formContact.use_contact}
                        />
                        <ContactIcons visible={['desktop', 'envelope']} />
                    </div>
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.phone_number')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Phone value={formContact.phone_number || ''} onChange={value => onChangeContact('phone_number', value)} disabled={!formContact.use_contact} />
                        <ContactIcons visible={['desktop', 'tickets']} />
                    </div>
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.mobile_number')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Phone value={formContact.mobile_number || ''} onChange={value => onChangeContact('mobile_number', value)} disabled={!formContact.use_contact} />
                        <ContactIcons visible={['desktop']} />
                    </div>
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.website')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Form.Input
                            name="website"
                            value={formContact.website || ''}
                            onChange={({ target: { name, value } }) => {
                                onChangeContact(name, value);
                            }}
                            disabled={!formContact.use_contact}
                            errors={eventErrors['contact_data']}
                        />
                        <ContactIcons visible={['desktop', 'envelope']} />
                    </div>
                </Form.Field>
                <Form.FieldErrors errors={[t(eventErrors['contact_data']?.website)]} />
                <Form.Field>
                    <Form.Label label={t('ticket.creationEvent.contact.blog')} optionalText={t('ticket.common.labelOptional')} />
                    <div className={Styles['contact-field-container']}>
                        <Form.Input
                            name="blog"
                            value={formContact.blog || ''}
                            onChange={({ target: { name, value } }) => {
                                onChangeContact(name, value);
                            }}
                            disabled={!formContact.use_contact}
                        />
                        <ContactIcons visible={['desktop', 'envelope']} />
                    </div>
                </Form.Field>
            </Section>
        </>
    );
}

EventCreationContact.propTypes = {
    formContact: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    defaultFormContact: PropTypes.object,
    eventErrors: PropTypes.object
};
